import { StyleSheet, css } from 'aphrodite';
import { useState } from 'react';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { AdminPermission } from '../../../viewmodels/AdminViewModels';
import { SupportIcon } from '../../../web/components/svgs/icons/SupportIcon';
import { Button } from '../../../web/components-v2/Button';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { NewTicketModal } from '../../components/supportShared/NewTicketModal';
import { styleSheet as accountDetailsStyleSheet } from '../../containers/account/AccountDetails/styles';

/** Flattens the components-v2 Button's fixed sizing so it sits inline with the header's other action buttons. */
const s = StyleSheet.create({
	button: {
		background: 'none',
		border: 'none',
		boxShadow: 'none',
		height: 'auto',
		padding: 0,
		':focus': {
			color: THEME.colors.text.light,
			textDecoration: 'none',
		},
		':hover': {
			color: THEME.colors.primary.main,
			textDecoration: 'none',
		},
	},
	icon: {
		width: 18,
		height: 18,
	},
});

export interface INewSupportTicketQuickActionProps {
	accountId: string;
	accountName?: string;
}

/**
 * Page-header "New Support Ticket" quick action. Opens the support desk's new-ticket modal
 * prefilled with this account (and therefore its CSM). Gated like the support routes:
 * support desk access or the developer permission.
 */
export const NewSupportTicketQuickAction = ({ accountId, accountName }: INewSupportTicketQuickActionProps) => {
	const userSession = useAdminUserSession();
	const [showModal, setShowModal] = useState(false);
	const hasSupportAccess =
		!!userSession.canAccessSupport || userSession.hasPermission(AdminPermission.OnlyForDevelopers);
	if (!hasSupportAccess) {
		return null;
	}

	return (
		<>
			<Button
				variant='link'
				styles={[accountDetailsStyleSheet.actionEntities, s.button]}
				onClick={() => setShowModal(true)}
			>
				<SupportIcon stroke={THEME.colors.secondary.main} className={css(s.icon)} />
				<span>New Support Ticket</span>
			</Button>
			{showModal && (
				<NewTicketModal defaultAccount={{ id: accountId, name: accountName }} onClose={() => setShowModal(false)} />
			)}
		</>
	);
};
